import { ArrowRight, Check, PencilSimple } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import { cn } from '../../utils/cn';
import pt from '../../locales/pt';

/**
 * Passo final de revisão — frames `Onboarding · Pronto` no `.pen`.
 *
 * Título + subtítulo + pills de resumo (`$sage-tint` / `$sage-500`,
 * `rounded-$radius-ds-full`, `padding [$space-3, $space-4]`), cada uma com o
 * afordance «Editar» (`$sage-on-tint`, ícone `pencil-simple` trailing) que
 * devolve o utilizador ao passo correspondente.
 *
 * `summary`: `[{ label, icon, value, editTo | onEdit, editLabel }]`.
 */
function EditLink({ row, className }) {
  const label = row.editLabel || pt.wizard.common.edit;
  const content = (
    <>
      {label}
      <PencilSimple className="size-3.5 shrink-0" weight="regular" aria-hidden />
    </>
  );

  if (row.editTo) {
    return (
      <Link to={row.editTo} className={className} aria-label={`${label} · ${row.label}`}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={row.onEdit} className={className} aria-label={`${label} · ${row.label}`}>
      {content}
    </button>
  );
}

export function WizardDoneStep({ title, subtitle, summary = [], nextTip, className }) {
  const copy = pt.wizard.common;

  return (
    <div className={cn('my-auto flex w-full flex-col gap-space-6', className)}>
      <div className="flex flex-col gap-space-3">
        <h1 className="m-0 font-display text-type-title-page font-semibold leading-[1.25] tracking-tight text-foreground">
          {title || copy.doneTitle}
        </h1>
        {subtitle ? (
          <p className="m-0 text-type-body leading-[1.55] text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>

      {summary.length > 0 ? (
        <div className="flex flex-col gap-space-2">
          {summary.map((row, index) => {
            const Icon = row.icon || Check;
            const canEdit = Boolean(row.editTo || row.onEdit);

            return (
              <div
                key={row.label || index}
                className="flex items-center gap-space-2-5 rounded-ds-full border border-sage-500 bg-sage-tint px-space-4 py-space-3"
              >
                <Icon className="size-[18px] shrink-0 text-sage-on-tint" weight="bold" aria-hidden />
                <span className="min-w-0 text-type-body-sm font-semibold leading-[1.4] text-sage-on-tint">
                  {row.label}
                </span>
                <span className="flex-1" aria-hidden />
                {row.value ? (
                  <span className="shrink-0 text-type-body-sm font-semibold tabular-nums text-sage-on-tint">
                    {row.value}
                  </span>
                ) : null}
                {canEdit ? (
                  <EditLink
                    row={row}
                    className="inline-flex shrink-0 items-center gap-space-1 text-type-label font-semibold leading-[1.4] text-sage-on-tint underline-offset-2 transition hover:underline"
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {nextTip ? (
        <div
          data-ds-id="ds/message--sage"
          className="flex items-start gap-space-3 rounded-ds-lg border border-sage-border bg-sage-tint px-space-3 py-space-2-5"
        >
          <ArrowRight className="mt-0.5 size-4 shrink-0 text-sage-on-tint" weight="regular" aria-hidden />
          <p className="m-0 text-type-body-sm leading-[1.5] text-sage-on-tint">
            <span className="font-semibold">{copy.nextLabel}</span> {nextTip}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default WizardDoneStep;
